import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Save, ArrowLeft, User, Mail, Loader2, KeyRound, ShieldAlert } from 'lucide-react';
import { getApiUrl } from '../utils/api';
import { ConfirmHardPasswordResetModal } from '../components/ConfirmHardPasswordResetModal';
import { useToast } from '../components/Toast';
import { RootState } from '../store';

export function UserEditPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { showToast, ToastComponent } = useToast();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const isSuperAdmin = String(currentUser?.role || '').toLowerCase().replace(/-/g, '_') === 'super_admin';
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string[]>([]);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    status: 'ACTIVE',
  });

  useEffect(() => {
    if (id) fetchUser();
  }, [id]);

  const fetchUser = async () => {
    try {
      setFetching(true);
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/admin/users/${id}`), {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        const u = data.data.user;
        setFormData({
          email: u.email,
          status: u.status,
        });
      } else {
        navigate('/users');
      }
    } catch {
      navigate('/users');
    } finally {
      setFetching(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setErrorDetails([]);
    if (!formData.email.trim()) {
      setError('L\'email est obligatoire.');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/admin/users/${id}`), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email.trim(),
          status: formData.status,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (data.success) {
        navigate('/users');
        return;
      }
      setError(data.message || 'Erreur lors de la mise à jour.');
      if (Array.isArray(data.errors)) {
        setErrorDetails(data.errors.map((err: any) => typeof err === 'string' ? err : err.message || JSON.stringify(err)));
      }
    } catch {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <ToastComponent />
      <div className="mb-6">
        <Link to="/users" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-4 h-4" />
          Retour à la liste
        </Link>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <User className="w-6 h-6 text-primary-600" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900">Modifier l'utilisateur</h1>
        </div>

        <form onSubmit={handleSubmit} autoComplete="off" className="px-6 py-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
              {errorDetails.length > 0 && (
                <ul className="mt-2 list-disc list-inside space-y-1">
                  {errorDetails.map((detail, i) => (
                    <li key={i}>{detail}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                  className="input-field pl-10 w-full"
                  placeholder="utilisateur@exemple.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value }))}
                className="input-field w-full"
              >
                <option value="ACTIVE">Actif</option>
                <option value="INACTIVE">Inactif</option>
                <option value="SUSPENDED">Suspendu</option>
                <option value="LOCKED">Verrouillé</option>
              </select>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
            <Link to="/users" className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
              Annuler
            </Link>
          </div>
        </form>
      </div>

      {isSuperAdmin && (
        <div className="mt-6 bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <KeyRound className="w-6 h-6 text-amber-700" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{t('users.passwordReset.action')}</h2>
              <p className="text-sm text-gray-500">{t('users.passwordReset.step1Subtitle')}</p>
            </div>
          </div>
          <div className="px-6 py-4 space-y-4">
            <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <ShieldAlert className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900">{t('users.passwordReset.step1Warning')}</p>
            </div>
            <button
              type="button"
              onClick={() => setShowPasswordReset(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
            >
              <KeyRound className="w-4 h-4" />
              {t('users.passwordReset.action')}
            </button>
          </div>
        </div>
      )}

      <ConfirmHardPasswordResetModal
        isOpen={showPasswordReset}
        userId={id ?? null}
        userEmail={formData.email}
        onClose={() => setShowPasswordReset(false)}
        onSuccess={() => {
          setShowPasswordReset(false);
          showToast(t('users.passwordReset.success'), 'success');
        }}
      />
    </div>
  );
}
